/**
 * Los 3 roles del portal (rediseño de set-2026) y la traducción de los grupos
 * de Cognito.
 *
 * Vive en su propio módulo, sin JSX, para que `AuthContext.tsx` exporte solo
 * componentes: mezclarlos rompe el fast refresh de Vite y cada cambio en el
 * mapa de roles recarga la página entera perdiendo la sesión.
 *
 *   asesor     — solo su bandeja de conversaciones asignadas (3 ítems de nav)
 *   supervisor — cola global del equipo, documentos, métricas (3 ítems)
 *   admin      — todo, con los módulos de sistema reagrupados
 */
export type PortalRole = 'asesor' | 'supervisor' | 'admin'

/** Etiqueta visible del rol. El nombre del grupo de Cognito es el mismo. */
export const ROLE_LABEL: Record<PortalRole, string> = {
  admin: 'Admin',
  supervisor: 'Supervisor',
  asesor: 'Asesor',
}

/**
 * Grupos del modelo anterior que ya no existen en el User Pool. Se traducen
 * igual porque los JWT emitidos antes de la migración viven hasta 60 min: sin
 * esto, quien tuviera la sesión abierta durante el deploy vería "Acceso
 * pendiente" hasta volver a logear.
 *
 * Mismo mapa que `portal_api/auth/principal.LEGACY_GROUP_ALIASES`. Se borran
 * los dos juntos cuando no queden sesiones viejas.
 */
const LEGACY_GROUPS: Record<string, string> = {
  Operator: 'Asesor',
  Reader: 'Asesor',
  Read: 'Asesor',
}

const ROLE_BY_GROUP: Record<string, PortalRole> = {
  Admin: 'admin',
  Supervisor: 'supervisor',
  Asesor: 'asesor',
}

/** Gana el rol de mayor alcance: quien es admin y asesor ve el portal de admin. */
const ROLE_PRIORITY: PortalRole[] = ['admin', 'supervisor', 'asesor']

export function roleFromGroups(groups: string[]): PortalRole | null {
  const roles = new Set<PortalRole>()
  for (const raw of groups) {
    const canonical = LEGACY_GROUPS[raw] ?? raw
    const role = ROLE_BY_GROUP[canonical]
    if (role) roles.add(role)
  }
  return ROLE_PRIORITY.find((r) => roles.has(r)) ?? null
}

/**
 * True si el usuario está en el grupo `Asesor` de Cognito, sin importar que
 * además sea admin o supervisor.
 *
 * El ROL efectivo es el de mayor alcance, pero la pertenencia al grupo Asesor
 * es lo que mira el round-robin (`shared/round_robin.list_asesores`). Alguien
 * que es Admin y Asesor a la vez RECIBE conversaciones, así que tiene que
 * poder verlas: sin esto le caen casos y no tiene el ítem en el sidebar.
 */
export function atiendeConversaciones(groups: string[]): boolean {
  return groups.some((g) => (LEGACY_GROUPS[g] ?? g) === 'Asesor')
}
